import type { IAngularEvent, IComponentController, ILogService, ITranscludeFunction } from "angular";
import { isDefined, isFunction } from "angular";

import type { IProfileService } from "App/Modules/Auth";
import type { IAction } from "App/Modules/Gui/Components/ActionMenu/IAction";
import type { IResponseWithLoading } from "App/Modules/Models";
import { Permission } from "App/Modules/Models";
import type { IButtonEvent } from "../Button/Common/IButtonEvent";
import { DefaultActions } from "../ActionMenu/DefaultActions";

export default class CardComponentController implements IComponentController {
    public static $inject = ["$transclude", "$log", "mmProfileService"];

    public caption?: string;

    public subCaption?: string;

    public cardClass?: string;

    public icon?: string;

    public iconTitle?: string;

    public image?: string;

    public url?: string;

    public click?: (data: IButtonEvent) => void;

    public response?: IResponseWithLoading<any>;

    public isLoading?: boolean;

    public noContent?: boolean;

    public noContentText?: string;

    public isExpandable?: boolean;

    public isExpanded?: boolean;

    public actions?: IAction[];

    public add?: (data: IButtonEvent) => void;

    public edit?: (data: IButtonEvent) => void;

    public delete?: (data: IButtonEvent) => void;

    public highlightColor: string;

    public actionsPermission?: string;

    public hasContent: boolean;

    public hasContentOnly: boolean;

    public hasTopContent: boolean;

    public hasActionsPermission: boolean;

    public hasSecondBody: boolean;

    public showButtonBar: boolean;

    public showSubCaption: boolean;

    public showTitleBlock: boolean;

    public linkCard: boolean;

    constructor(
        private readonly $transclude: ITranscludeFunction,
        private readonly $log: ILogService,
        private readonly profileService: IProfileService,
    ) {}

    public $onInit() {
        if (!isDefined(this.actions)) {
            this.actions = [];
        }

        if (isDefined(this.actionsPermission)) {
            const permissionEnum = Permission[this.actionsPermission as keyof typeof Permission];
            if (!permissionEnum) {
                throw new TypeError(`CardComponent: Unknown permission :${this.actionsPermission}`);
            }
            this.hasActionsPermission = this.profileService.hasPermission(permissionEnum);
        } else if (!isDefined(this.hasActionsPermission) && !isDefined(this.actionsPermission)) {
            this.hasActionsPermission = true;
        }

        if (this.hasActionsPermission && isDefined(this.add)) {
            const addAction = { ...DefaultActions.AddAction, click: this.add };
            this.actions.push(addAction);
        }
        if (this.hasActionsPermission && isDefined(this.edit)) {
            const editAction = { ...DefaultActions.EditAction, click: this.edit };
            this.actions.push(editAction);
        }
        if (this.hasActionsPermission && isDefined(this.delete)) {
            const deleteAction = { ...DefaultActions.DeleteAction, click: this.delete };
            this.actions.push(deleteAction);
        }

        this.updateBindings();
    }

    public $onChanges() {
        this.updateBindings();
    }

    public cardClick($event: IAngularEvent) {
        if (isFunction(this.click)) {
            this.click({ $event });
        }
        if (!isDefined(this.url)) {
            $event.preventDefault();
        }
    }

    private updateBindings() {
        this.noContentText = isDefined(this.noContentText) ? this.noContentText : "Gui.Common.NoItemsAvailable";

        if (!isDefined(this.actions)) {
            this.actions = [];
        }

        if (isDefined(this.response)) {
            this.isLoading = this.response.isLoading;
        }

        this.showSubCaption =
            (isDefined(this.subCaption) && isDefined(this.icon)) || isDefined(this.subCaption) || this.noContent;
        this.showTitleBlock = isDefined(this.caption) || this.isLoading || this.noContent;
        this.showButtonBar = this.isExpandable || (this.actions && this.actions.length !== 0);
        this.linkCard = this.hasActionsPermission && (isDefined(this.url) || isDefined(this.click));
        this.hasContentOnly = !(this.showButtonBar || this.showTitleBlock);

        this.hasContent = this.$transclude.isSlotFilled("content");
        this.hasTopContent = this.$transclude.isSlotFilled("topContent");
        this.hasSecondBody = this.$transclude.isSlotFilled("secondBody");
    }
}
